import React, { useEffect, useState } from "react";
import { ModalBody, Spinner, Text, Textarea } from "@chakra-ui/react";
import queryStrings from "query-string";
import { formatDistanceToNow } from "date-fns";
import {
  CaptionWrapper,
  AuthorWrapper,
  Author,
  SharedPostWrapper,
  CommentButton,
  NameAndTime,
} from "./styles";
import { Comment, Share, VerticalMoreIcon } from "../../Assets/SvgIcons";
import { Reactions } from "../Reactions/Reactions";
import { usePost } from "../../hooks/usePost";
import { Modal } from "../Modal/Modal";
import { FloatingDropdown } from "../FloatingDropdown/FloatingDropdown";
import { useNavigate } from "react-router-dom";
import { customConsole, truncate } from "../../utils/helper";
import Metric from "../Metric/Metric";
import { Post } from "../../../redux/reducers/types";
import { Comments } from "../Comments/Comments";
import { ProfileAvatar } from "../ProfileAvatar/ProfileAvatar";
import { PostCard } from "./PostCard";
import { useUpdatePostMutation } from "../../../redux/reducers/apiSlice";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/react";

const MAX_CHAR_COUNT = 2500;

type Props = {
  post: Post;
  onShare: () => void;
  commentsFullHeight?: boolean;
};

const SharedPost: React.FC<Props> = ({ onShare, post, commentsFullHeight }) => {
  const [isFloatingDropdownVisible, setIsFloatingDropdownVisible] =
    useState(false);
  const [showComment, setShowComment] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedCaption, setEditedCaption] = useState(post?.caption || "");
  const [isHovered, setIsHovered] = useState(false);
  const [updatePost, { isLoading: updatePostLoading }] = useUpdatePostMutation();
  const {
    comments,
    handleDeletePost,
    deletePostLoading,
    sharedPost,
    sharedPostLoading,
    commentCount,
    sharedMetric,
    optionsEnabled,
  } = usePost(post);

  useEffect(() => {
    const queryString = queryStrings.parse(location.search);
    if (queryString?.showComment && queryString.showComment === "true") {
      setShowComment(true);
    }
  }, [location.search]);

  customConsole("log", "OPTIONS ENABLED", optionsEnabled);

  const navigate = useNavigate();

  const isTruncated =
    (post?.ownerType === "USER" && post?.ownerName?.length > 68) ||
    (post?.ownerType === "ORGANIZATION" && post?.ownerName?.length > 127);

  const isEdited =
    !!post?.updatedAt &&
    !!post?.createdAt &&
    new Date(post.updatedAt).getTime() > new Date(post.createdAt).getTime();

  useEffect(() => {
    if (showEditModal) {
      setEditedCaption(post?.caption || "");
    }
  }, [showEditModal, post?.caption]);

  const handleSavePostEdit = async () => {
    const trimmedCaption = editedCaption.trim();
    const hasCaptionChanged = trimmedCaption !== (post?.caption || "").trim();

    if (!hasCaptionChanged) {
      setShowEditModal(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("caption", trimmedCaption);
      await updatePost({ id: post.id, postData: formData }).unwrap();
      toast.success("Post updated successfully");
      setShowEditModal(false);
      setIsFloatingDropdownVisible(false);
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Unable to update this post. Please try again.");
    }
  };

  const normalizedOriginalCaption = (post?.caption || "").trim();
  const normalizedEditedCaption = editedCaption.trim();
  const hasUnsavedChanges = normalizedEditedCaption !== normalizedOriginalCaption;
  const remainingCharacters = MAX_CHAR_COUNT - editedCaption.length;
  const isSaveDisabled =
    updatePostLoading || editedCaption.length > MAX_CHAR_COUNT || !hasUnsavedChanges;

  const handleCloseEditModal = () => {
    if (hasUnsavedChanges) {
      const shouldDiscard = window.confirm("Discard your post changes?");
      if (!shouldDiscard) return;
    }
    setShowEditModal(false);
  };

  return (
    <SharedPostWrapper>
      <AuthorWrapper>
        <Author
          onClick={() =>
            navigate(
              post.ownerType === "USER"
                ? `/users/${post?.ownerId}`
                : `/organizations/${post.ownerId}/profile`
            )
          }
        >
          <ProfileAvatar
            border={`2px solid #eee`}
            name={post?.ownerName}
            src={post?.ownerProfilePhoto}
          />
          <NameAndTime
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            $isHoveredAndTruncated={isHovered && isTruncated}
          >
            <span className="name">
              {post?.ownerType === "USER" &&
                post?.ownerName &&
                !isHovered &&
                truncate(post?.ownerName)}
              {post?.ownerType === "ORGANIZATION" &&
                post?.ownerName &&
                !isHovered &&
                truncate(post?.ownerName, 127)}
              {isHovered && post?.ownerName}
            </span>
            <span className="time">
              {post?.updatedAt &&
                formatDistanceToNow(new Date(post.updatedAt), {
                  addSuffix: true,
                }).replace("about", "")}
              {isEdited ? " • Edited" : ""}
            </span>
          </NameAndTime>
        </Author>
        <div className="more-icon-container" id="cypress-menu-tap">
          <FloatingDropdown
            items={[
              {
                label: "Edit post",
                action: () => {
                  setShowEditModal(true);
                  setIsFloatingDropdownVisible(false);
                },
              },
              {
                label: "Delete post",
                action: () => setShowDeleteModal(true),
                isDestructiveAction: true,
              },
            ]}
            onOutsideClicked={() => setIsFloatingDropdownVisible(false)}
            visible={isFloatingDropdownVisible}
          />
          <button
            disabled={!optionsEnabled}
            onClick={() => {
              setIsFloatingDropdownVisible((currentlyVisible) => !currentlyVisible);
            }}
          >
            <VerticalMoreIcon color={optionsEnabled ? "black" : "#eee"} />
          </button>
        </div>
      </AuthorWrapper>
      {post?.caption && <CaptionWrapper>{post?.caption}</CaptionWrapper>}
      <div>
        {sharedPostLoading ? (
          <Spinner color="red" />
        ) : (
          <>
            {sharedPost && !sharedMetric && (
              <PostCard sharedPost={true} post={sharedPost} />
            )}
            {sharedMetric && <Metric metric={sharedMetric} isShared />}
          </>
        )}
      </div>

      <div className="divider" />
      <div className="actions-container">
        <button onClick={() => onShare()} className="icon" id="cypress-share-post">
          <Share />
        </button>
        <CommentButton
          onClick={() => setShowComment((showComment) => !showComment)}
          className="icon"
          id="cypress-comment-a-post"
        >
          <Comment /> <span className="comment-count">{commentCount}</span>
        </CommentButton>
        <Reactions post={post} />
      </div>
      <Comments
        fullHeight={commentsFullHeight}
        postId={post?.id}
        comments={comments}
        visible={showComment}
      />

      <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onPrimaryButtonClicked={() => {
            setIsFloatingDropdownVisible(false);
            handleDeletePost();
          }}
          modalTitle="Are you sure you want to delete this post?"
          primaryLoading={deletePostLoading}
          disablePrimaryButton={deletePostLoading}
          primaryButtonText="Delete"
          secondaryButtonText="Cancel"
        />
      <Modal
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          onPrimaryButtonClicked={handleSavePostEdit}
          modalTitle="Edit post"
          modalSubtitle="Make changes to your post. Edits are visible right away."
          primaryLoading={updatePostLoading}
          disablePrimaryButton={isSaveDisabled}
          primaryButtonText="Save"
          secondaryButtonText="Cancel"
        >
          <ModalBody paddingBottom="20px">
            <Textarea
              autoFocus
              value={editedCaption}
              onChange={(event) => {
                if (event.target.value.length <= MAX_CHAR_COUNT) {
                  setEditedCaption(event.target.value);
                }
              }}
              placeholder="What's happening?"
              minHeight="120px"
              resize="vertical"
            />
            <Text
              textAlign="right"
              marginTop="6px"
              fontSize="12px"
              color={remainingCharacters <= 100 ? "orange.500" : "gray.500"}
            >
              {remainingCharacters} characters remaining
            </Text>
          </ModalBody>
        </Modal>
    </SharedPostWrapper>
  );
};

export default SharedPost;
